import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Container } from 'react-bootstrap';
import { renderMarkdown, excerpt } from '../utils/markdown';
import './blog.css';

const FETCH_TIMEOUT_MS = 10000;

const DetailSkeleton = () => (
  <Container className="py-5" style={{ maxWidth: '820px' }}>
    <div className="blog-skel blog-skel-line w-40" style={{ height: 14 }} />
    <div className="blog-skel blog-skel-detail-title" />
    <div className="blog-skel blog-skel-detail-meta" />
    <div className="blog-skel blog-skel-detail-hero" />
    <div className="blog-skel blog-skel-detail-para w-80" />
    <div className="blog-skel blog-skel-detail-para" />
    <div className="blog-skel blog-skel-detail-para w-60" />
    <div className="blog-skel blog-skel-detail-para w-80" style={{ marginTop: '1.5rem' }} />
    <div className="blog-skel blog-skel-detail-para" />
  </Container>
);

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    const fetchPost = async () => {
      setLoading(true);
      setFailed(false);
      setNotFound(false);
      try {
        const params = new URLSearchParams({ 'filters[slug][$eq]': slug });
        const res = await fetch(`/api/blogs?${params.toString()}`, { signal: controller.signal });
        const json = await res.json();
        if (cancelled) return;
        if (json.error || !res.ok) throw new Error(json.error || 'Request failed');

        const found = json.data?.[0];
        if (!found) { setNotFound(true); return; }

        if (found.redirecturl) {
          window.location.replace(found.redirecturl);
          return;
        }

        setPost(found);
      } catch (err) {
        if (!cancelled) setFailed(true);
      } finally {
        clearTimeout(timeoutId);
        if (!cancelled) setLoading(false);
      }
    };

    if (slug) fetchPost();
    return () => { cancelled = true; clearTimeout(timeoutId); controller.abort(); };
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) return <DetailSkeleton />;

  if (failed) {
    return (
      <Container>
        <div className="blog-fallback">
          <div className="blog-fallback-icon">🚧</div>
          <h3>Coming Soon</h3>
          <p>This post is having trouble loading right now. Please check back shortly.</p>
          <button className="btn-retry" onClick={() => navigate('/blog')}>← Back to Blog</button>
        </div>
      </Container>
    );
  }

  if (notFound || !post) {
    return (
      <Container>
        <div className="blog-fallback">
          <div className="blog-fallback-icon">📝</div>
          <h3>Post Not Found</h3>
          <p>This post may have been moved or unpublished.</p>
          <button className="btn-retry" onClick={() => navigate('/blog')}>← Back to Blog</button>
        </div>
      </Container>
    );
  }

  const metaTitle = post.metaTitle || post.title;
  const metaDescription = post.metaDescription || excerpt(post.content, 160);

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", background: '#fafbfe' }}>
      <Helmet>
        <title>{metaTitle} | SpireNest</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://spirenest.co/blog/${post.slug}`} />
        {post.faqSchema && (
          <script type="application/ld+json">{post.faqSchema}</script>
        )}
      </Helmet>

      <Container className="py-5" style={{ maxWidth: '820px' }}>
        <Link to="/blog" style={{ color: '#3b6fe0', fontWeight: 600, textDecoration: 'none', fontSize: '.88rem' }}>
          ← Back to Blog
        </Link>

        <div className="mt-3 mb-1">
          {post.postType === 'newsletter' && <span className="blog-badge-soft">Newsletter</span>}
        </div>
        <h1 style={{ fontWeight: 800, fontSize: '2.15rem', lineHeight: 1.25, margin: '.25rem 0 .6rem', color: '#1a2236' }}>
          {post.title}
        </h1>
        <p style={{ color: '#98a4bd', fontSize: '.9rem', marginBottom: '2rem' }}>{formatDate(post.datetime)}</p>

        {post.image && (
          <img
            src={post.image}
            alt={post.title}
            style={{
              width: '100%',
              maxHeight: '440px',
              objectFit: 'cover',
              borderRadius: '16px',
              marginBottom: '2.25rem',
              boxShadow: '0 8px 30px rgba(15,23,42,.08)',
            }}
          />
        )}

        <div
          className="blog-content-body"
          style={{
            background: '#ffffff',
            border: '1px solid #eef2fa',
            borderRadius: '16px',
            padding: '2rem 2.25rem',
            fontSize: '1.03rem',
            lineHeight: 1.85,
            color: '#3a4460',
            boxShadow: '0 4px 18px rgba(15,23,42,.04)',
          }}
          dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }}
        />
      </Container>
    </div>
  );
};

export default BlogDetail;
